import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { dialPhone } from '@/services/nativeBridge';
import { REFETCH_RESPONSES_MS } from '@/shared/timing';
import {
  type ResponsesData, type RespUser,
  VVF_GRADE_SHORT, formatCompetenze,
} from './types';

/**
 * Sub-modal di ActiveSelectiveItem: mostra l'elenco utenti per uno dei tre
 * filtri (accepted / rejected / noResponse). Per gli accepted aggiunge il
 * badge grado VVF e le competenze (Autista, TPSS, ATP, SAF, NBCR, TAS).
 */
export function SelectiveUserListModal({ selectiveId, filter, onClose }: {
  selectiveId: string;
  filter: 'accepted' | 'rejected' | 'noResponse';
  onClose: () => void;
}): React.JSX.Element {
  const { data, isLoading } = useQuery<ResponsesData>({
    queryKey: ['selective.responses', selectiveId],
    queryFn: async () => (await api.get(`/selectives/${selectiveId}/responses`)).data,
    refetchInterval: REFETCH_RESPONSES_MS,
  });

  const list = data ? data[filter] : [];
  const title =
    filter === 'accepted'   ? `✅ Hanno accettato (${list.length})` :
    filter === 'rejected'   ? `❌ Hanno rifiutato (${list.length})` :
                              `⏳ Non hanno risposto (${list.length})`;
  const borderColor =
    filter === 'accepted'   ? '#28a745' :
    filter === 'rejected'   ? '#dc3545' :
                              '#ffc107';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <h3 style={{ marginBottom: 16 }}>{title}</h3>
        <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 15 }}>
          {isLoading && <p style={{ textAlign: 'center' }}>Caricamento...</p>}
          {!isLoading && list.length === 0 && (
            <p style={{ textAlign: 'center', color: '#6c757d', padding: 30 }}>Nessun utente</p>
          )}
          {list.map(u => {
            const isAcceptedOrArrived = filter === 'accepted';
            const profile = u as RespUser;
            const competenze = isAcceptedOrArrived ? formatCompetenze(profile) : [];
            const gradeBadge = isAcceptedOrArrived && profile.vvfGrade ? VVF_GRADE_SHORT[profile.vvfGrade] : null;
            return (
              <div key={u.userId} style={{
                padding: 12, margin: '6px 0', background: '#f9f9f9', borderRadius: 8,
                borderLeft: `4px solid ${borderColor}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 15 }}>{u.nickname}</strong>
                  {gradeBadge && (
                    <span style={{ background: '#dc3545', color: 'white', borderRadius: 4,
                      padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                      {gradeBadge}
                    </span>
                  )}
                  {competenze.map(c => (
                    <span key={c} style={{ background: '#e7f3ff', color: '#0056b3',
                      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                      {c}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {(u as RespUser).phoneNumber
                    ? <a href={`tel:${(u as RespUser).phoneNumber}`}
                         onClick={e => { e.preventDefault(); dialPhone((u as RespUser).phoneNumber ?? ''); }}
                         style={{ color: '#0056b3', textDecoration: 'none', fontWeight: 600 }}>
                        📞 {(u as RespUser).phoneNumber}
                      </a>
                    : u.email}
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={onClose}
          style={{ width: '100%', padding: 12, fontSize: 16, fontWeight: 'bold',
                   border: 'none', borderRadius: 8, cursor: 'pointer',
                   background: '#6c757d', color: 'white' }}>
          Chiudi
        </button>
      </div>
    </div>
  );
}
